import React from "react";

// Hand-placed dot clusters approximating continent shapes for a premium fintech world map.
// Coordinates are in a 1000x500 viewBox.
const continentDots = [
  // North America
  [180,110],[200,115],[220,120],[170,130],[195,135],[225,140],[155,155],[180,158],[210,160],[235,165],
  [165,175],[195,180],[225,185],[180,200],[210,205],[170,220],[200,225],[225,228],[185,245],[215,250],
  // Central America / Caribbean
  [220,265],[235,275],[245,285],[230,290],
  // South America
  [275,295],[285,310],[295,325],[280,340],[300,355],[290,375],[310,380],[285,400],[300,415],[290,430],
  [305,445],
  // Europe
  [490,135],[505,140],[520,145],[475,150],[495,155],[515,160],[505,170],[485,175],[505,185],[525,180],
  [495,195],[515,200],
  // Africa
  [510,225],[530,235],[505,250],[525,260],[545,270],[510,285],[530,295],[550,305],[515,320],[535,330],
  [525,350],[510,365],[530,375],[520,395],
  // Middle East
  [565,215],[580,225],[595,235],[570,245],[585,250],
  // Asia (large landmass)
  [620,135],[640,140],[660,145],[680,140],[700,145],[720,150],[740,155],[760,150],[780,160],[800,165],
  [625,165],[650,170],[675,168],[700,170],[725,175],[750,180],[775,180],[800,185],
  [640,190],[665,195],[690,195],[715,200],[740,205],[765,200],[790,210],
  [660,220],[685,225],[710,228],[735,230],[760,235],
  [680,250],[700,255],[720,255],[745,260],
  [710,275],[735,280],
  // South-east Asia / India
  [665,265],[680,275],[695,285],[710,295],[695,305],[680,315],
  // Indonesia / Australia
  [770,310],[790,320],[810,325],[795,340],[775,350],
  [820,365],[840,370],[860,375],[835,385],[855,390],[820,400],[845,405],[825,415],[860,415],
  // Japan
  [820,200],[830,215],[840,225],
  // UK
  [475,135],[485,145],
];

// Active financial hubs (will pulse) — [x, y, label]
const hubs = [
  { x: 195, y: 175, label: "New York" },
  { x: 495, y: 165, label: "London" },
  { x: 590, y: 220, label: "Dubai" },
  { x: 685, y: 285, label: "Mumbai" },
  { x: 750, y: 235, label: "Hong Kong" },
  { x: 820, y: 205, label: "Tokyo" },
  { x: 770, y: 290, label: "Singapore" },
];

// Cross-globe connection arcs (great-circle style cubic curves)
const arcs = [
  { from: 0, to: 1, dur: 6 },  // NYC → London
  { from: 1, to: 2, dur: 5.5 }, // London → Dubai
  { from: 2, to: 3, dur: 4.5 }, // Dubai → Mumbai
  { from: 3, to: 6, dur: 5 },   // Mumbai → Singapore
  { from: 4, to: 5, dur: 4 },   // HK → Tokyo
  { from: 0, to: 3, dur: 8 },   // NYC → Mumbai (long)
];

function arcPath(a, b) {
  const mx = (a.x + b.x) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const curve = Math.min(dist * 0.35, 110);
  const my = (a.y + b.y) / 2 - curve;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export default function WorldMap({ className = "" }) {
  return (
    <svg
      viewBox="0 0 1000 500"
      preserveAspectRatio="xMidYMid slice"
      className={`absolute inset-0 w-full h-full ${className}`}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="wm-dot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.18" />
        </radialGradient>
        <linearGradient id="wm-arc" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#0EA5E9" stopOpacity="0" />
          <stop offset="50%" stopColor="#7DD3FC" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="wm-hub" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FDE68A" stopOpacity="1" />
          <stop offset="60%" stopColor="#D4AF37" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* dotted continents */}
      {continentDots.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.4" fill="url(#wm-dot)" />
      ))}

      {/* Hub pulse rings */}
      {hubs.map((h, i) => (
        <g key={i}>
          <circle cx={h.x} cy={h.y} r="3" fill="#D4AF37">
            <animate attributeName="r" values="3;5;3" dur="2.4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.6;1" dur="2.4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={h.x} cy={h.y} r="6" fill="none" stroke="#D4AF37" strokeWidth="1" opacity="0.6">
            <animate attributeName="r" values="6;22;6" dur="3.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      {/* Animated arcs */}
      {arcs.map((a, i) => {
        const d = arcPath(hubs[a.from], hubs[a.to]);
        return (
          <g key={i}>
            <path d={d} stroke="url(#wm-arc)" strokeWidth="1.4" fill="none" opacity="0.45" />
            <circle r="2.6" fill="#7DD3FC">
              <animateMotion dur={`${a.dur}s`} repeatCount="indefinite" begin={`${i * 0.6}s`} path={d} />
              <animate attributeName="opacity" values="0;1;1;0" dur={`${a.dur}s`} begin={`${i * 0.6}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
    </svg>
  );
}
